import { FC } from 'react'
import { Box, SelectChangeEvent } from '@mui/material'

/* Utils */
import {
  ChartData,
  CPIFrequency,
  CPISeverity,
  CPITimePeriod,
  CustomLabelsDataPayload,
  CustomTrendsDataPayload,
  DataStatus,
  PredictionChartData,
  PredictionData,
  PredictionDataExtended,
  TrendsDataPayload
} from 'models'
import {
  filterPredictionData,
  formatCustomTimePickerValue,
  getIntegrationIds,
  getPayloadTimePeriod,
  getPayloadTrendsChart
} from '../../../../library/utils'
import { useLibraryDetails } from 'contexts/LibraryDetailsContext'
import theme from 'theme'

/* Components */
import CustomTimePicker from 'ui/components/custom-time-picker'
import TrendsChartSelectField from './TrendsChartSelectField'
import LibraryDataSourceSelect from './LibraryDataSourceSelect'
import { getDataSourceAllOption, getManifestDefaultFilters } from '../../../../library/library.constants'
import { DataTrendsControlsContainer, ControlsSection } from '../trendsTab.styles'
import ThemeButton from '../../../../../components/buttons/theme/Button'

interface Props {
  renderSeverities: () => JSX.Element | null
  fetchTrendsData: (payload: TrendsDataPayload, stripes?: number | null | undefined) => Promise<ChartData | undefined>
  hasPredictionsEnabled: boolean
  cpiName: string
  fetchCustomLabelsCount: (payload: CustomLabelsDataPayload, perid?: CPITimePeriod) => void
  updateLabels: (payload?: CustomLabelsDataPayload) => Promise<void>
}

const DataTrendsControls: FC<Props> = ({
  renderSeverities,
  fetchTrendsData,
  hasPredictionsEnabled,
  cpiName,
  // fetchCustomLabelsCount,
  updateLabels
}) => {
  const {
    isSnowflake,
    chartData,
    renderingChart,
    selectedType,
    setSelectedType,
    manifestData,
    setChartData,
    setRenderingChart,
    selectedTimePeriod,
    selectedFrequency,
    selectedSeverity,
    setSelectedSeverity,
    setSelectedFrequency,
    selectedDataSourceIds,
    setSelectedDataSourceIds,
    setSelectedTimePeriod,
    setPredictionData,
    fetchPredictionData,
    setDataSourceValue,
    timePeriodValues,
    dataSourceOptions,
    setCustomTimePeriodValue,
    frequencyValues,
    dataSourceValue,
    customTimePeriodValue,
    predictionModalOpen
  } = useLibraryDetails()
  if (!manifestData) return null
  const allOption = getDataSourceAllOption(dataSourceOptions.length)
  const allOptionText = `All (${dataSourceOptions.length})`

  const handleChangeTimePeriod = async (event: SelectChangeEvent | null, customPayload?: any) => {
    setRenderingChart(true)
    try {
      const period = event ? (event.target.value as CPITimePeriod) : CPITimePeriod.Custom
      setSelectedTimePeriod(period)
      let thisFrequency = selectedFrequency

      if (period === CPITimePeriod.Week) {
        thisFrequency = CPIFrequency.Daily
        setSelectedFrequency(CPIFrequency.Daily)
      }
      if (period === CPITimePeriod.Month && selectedFrequency === CPIFrequency.Monthly) {
        thisFrequency = CPIFrequency.Weekly
        setSelectedFrequency(CPIFrequency.Weekly)
      }

      const integrationIds = getIntegrationIds(selectedDataSourceIds, isSnowflake, dataSourceOptions)

      const payload: TrendsDataPayload = {
        section: selectedSeverity.toLowerCase() as CPISeverity,
        integration_ids: integrationIds,
        frequency: thisFrequency,
        type: selectedType.toLowerCase()
      }

      if (period === CPITimePeriod.Custom) {
        const { dateFrom, dateTo } = customPayload

        const formattedValue = formatCustomTimePickerValue(dateFrom, dateTo)
        setCustomTimePeriodValue(formattedValue)
        payload.dateFrom = dateFrom
        payload.dateTo = dateTo
      } else {
        setCustomTimePeriodValue('')
        payload.timePeriod = getPayloadTimePeriod(period)
      }

      if (customPayload && customPayload.stripes) {
        const dataTrendsRes = await fetchTrendsData(payload, customPayload.stripes)
        setChartData(dataTrendsRes ?? null)
      } else {
        const dataTrendsRes = await fetchTrendsData(payload)
        setChartData(dataTrendsRes ?? null)
      }

      const labelsPayload: CustomLabelsDataPayload = {
        ...(payload.timePeriod && {
          time_period: payload.timePeriod
        }),
        ...(payload.dateFrom &&
          payload.dateTo && {
            date_from: payload.dateFrom,
            date_to: payload.dateTo
          }),
        frequency: selectedFrequency
      }

      await updateLabels(labelsPayload)
    } catch (e) {
      console.error(e)
      setRenderingChart(false)
    }
  }

  const checkTimePeriodDisabled = (): boolean => {
    return chartData?.status !== DataStatus.Ready
  }

  const handleChangeFrequency = async (event: SelectChangeEvent) => {
    setRenderingChart(true)
    try {
      const frequency = event.target.value as CPIFrequency
      setSelectedFrequency(frequency)
      let thisTimePeriod = selectedTimePeriod

      const integrationIds = getIntegrationIds(selectedDataSourceIds, isSnowflake, dataSourceOptions)

      if (frequency === CPIFrequency.Weekly && selectedTimePeriod === CPITimePeriod.Week) {
        thisTimePeriod = CPITimePeriod.Month
        setSelectedTimePeriod(CPITimePeriod.Month)
      } else if (
        frequency === CPIFrequency.Monthly &&
        (selectedTimePeriod === CPITimePeriod.Week || selectedTimePeriod === CPITimePeriod.Month)
      ) {
        thisTimePeriod = CPITimePeriod.ThreeMonths
        setSelectedTimePeriod(CPITimePeriod.ThreeMonths)
      }

      const payload: CustomTrendsDataPayload = getPayloadTrendsChart({
        severity: selectedSeverity.toLowerCase() as CPISeverity,
        selectedDataSourceIds: integrationIds,
        selectedTimePeriod: thisTimePeriod,
        selectedFrequency: frequency,
        customTimePeriodValue,
        thisType: selectedType
      })
      const { stripes } = payload
      if (typeof stripes !== 'undefined') {
        delete payload.stripes
      }

      const dataTrendsRes = await fetchTrendsData(payload, stripes ?? undefined)
      setChartData(dataTrendsRes ?? null)
    } catch (e) {
      console.error(e)
      setRenderingChart(false)
    }
  }

  const checkFrequencyDisabled = (): boolean => {
    return chartData?.status !== DataStatus.Ready
  }

  const handleChangeType = async (event: SelectChangeEvent) => {
    setRenderingChart(true)
    try {
      const thisType = event.target.value
      setSelectedType(thisType)

      const integrationIds = getIntegrationIds(selectedDataSourceIds, isSnowflake, dataSourceOptions)

      const payload: CustomTrendsDataPayload = getPayloadTrendsChart({
        severity: selectedSeverity,
        selectedDataSourceIds: integrationIds,
        selectedTimePeriod,
        selectedFrequency,
        customTimePeriodValue,
        thisType: thisType === 'All' ? undefined : thisType
      })
      if (payload.type === 'all') {
        payload.type = undefined
      }
      const { stripes } = payload
      if (typeof stripes !== 'undefined') {
        delete payload.stripes
      }

      const dataTrendsRes = await fetchTrendsData(payload, stripes ?? undefined)
      setChartData(dataTrendsRes ?? null)
    } catch (e) {
      console.error(e)
      setRenderingChart(false)
    }
  }

  const handleChangeDataSource = async (event: SelectChangeEvent) => {
    setRenderingChart(true)
    try {
      const {
        target: { value }
      } = event

      const findItemsIds = dataSourceOptions.reduce((ids: string[], item) => {
        if (isSnowflake) {
          if (item.name === value) {
            ids.push(item.instance_name || '')
          }
        } else {
          if (item.name === value) {
            ids.push(item.id)
          }
        }

        return ids
      }, [])

      const selectedSources =
        value === allOptionText && isSnowflake
          ? dataSourceOptions.map((c) => c.instance_name || '')
          : value === allOptionText
          ? []
          : findItemsIds
      setSelectedDataSourceIds(selectedSources)
      setDataSourceValue(value === allOptionText ? `All (${dataSourceOptions.length})` : value)

      const payload: CustomTrendsDataPayload = getPayloadTrendsChart({
        severity: selectedSeverity.toLowerCase() as CPISeverity,
        selectedDataSourceIds: selectedSources,
        selectedTimePeriod,
        selectedFrequency,
        customTimePeriodValue,
        thisType: selectedType
      })

      const { stripes } = payload
      if (typeof stripes !== 'undefined') {
        delete payload.stripes
      }

      const dataTrendsRes = await fetchTrendsData(payload, stripes ?? undefined)
      setChartData(dataTrendsRes ?? null)

      if (hasPredictionsEnabled) {
        fetchPredictionData(
          cpiName || '',
          selectedSeverity,
          selectedSources.length === 1 ? selectedSources[0] : 'all'
        ).then((predictionValues: PredictionData[]) => {
          if (dataTrendsRes) {
            const allValues: PredictionDataExtended[] = [
              ...dataTrendsRes.values.map((n) => ({ xValue: n.xValue, yValue: n.yValue, prediction: false })),
              ...predictionValues.map((n) => ({ ...n, prediction: true }))
            ]

            const chartData: PredictionChartData = {
              ...dataTrendsRes,
              values: allValues
            }
            const filteredData: PredictionDataExtended[] = filterPredictionData(2, chartData)
            setPredictionData({
              ...chartData,
              values: [...filteredData]
            })
          }
        })
      }
    } catch (e) {
      console.error(e)
      setRenderingChart(false)
    }
  }

  const defaultFilters = getManifestDefaultFilters(manifestData)
  const isAllDataSources = dataSourceValue.startsWith('All')
  const isDirty =
    selectedSeverity !== defaultFilters.severity ||
    selectedFrequency !== defaultFilters.frequency ||
    selectedTimePeriod !== defaultFilters.timePeriod ||
    (Boolean(defaultFilters.type) && selectedType !== defaultFilters.type) ||
    Boolean(customTimePeriodValue) ||
    !isAllDataSources

  // Returns every filter to its manifest default and refetches the chart + labels, reusing
  // the same fetchTrendsData/updateLabels paths the individual change handlers use.
  const handleResetAll = async () => {
    setRenderingChart(true)
    try {
      const allSources = isSnowflake ? dataSourceOptions.map((c) => c.instance_name || '') : []

      setSelectedSeverity(defaultFilters.severity)
      setSelectedFrequency(defaultFilters.frequency)
      setSelectedTimePeriod(defaultFilters.timePeriod)
      if (defaultFilters.type) setSelectedType(defaultFilters.type)
      setCustomTimePeriodValue('')
      setSelectedDataSourceIds(allSources)
      setDataSourceValue(`All (${dataSourceOptions.length})`)

      const payload: CustomTrendsDataPayload = getPayloadTrendsChart({
        severity: defaultFilters.severity.toLowerCase() as CPISeverity,
        selectedDataSourceIds: allSources,
        selectedTimePeriod: defaultFilters.timePeriod,
        selectedFrequency: defaultFilters.frequency,
        customTimePeriodValue: '',
        thisType: defaultFilters.type || undefined
      })
      const { stripes } = payload
      if (typeof stripes !== 'undefined') {
        delete payload.stripes
      }

      const dataTrendsRes = await fetchTrendsData(payload, stripes ?? undefined)
      setChartData(dataTrendsRes ?? null)

      const labelsPayload: CustomLabelsDataPayload = {
        ...(payload.timePeriod && { time_period: payload.timePeriod }),
        frequency: defaultFilters.frequency
      }
      await updateLabels(labelsPayload)
    } catch (e) {
      console.error(e)
      setRenderingChart(false)
    }
  }

  return (
    <DataTrendsControlsContainer className="data-trends-controls-container">
      {renderSeverities() !== null && <ControlsSection>{renderSeverities()}</ControlsSection>}

      {!predictionModalOpen && (
        <ControlsSection predictionModalOpen={predictionModalOpen ? 'true' : 'false'}>
          <CustomTimePicker
            timePeriodValues={timePeriodValues}
            selectedTimePeriod={selectedTimePeriod}
            handleChangeTimePeriod={handleChangeTimePeriod}
            customTimePeriodValue={customTimePeriodValue}
            checkTimePeriodDisabled={checkTimePeriodDisabled}
            hideSelected
            disabled={Boolean(renderingChart)}
            inlineLabel="Time Period:"
          />
        </ControlsSection>
      )}

      {!predictionModalOpen && (
        <ControlsSection>
          <TrendsChartSelectField
            options={frequencyValues}
            label=""
            placeholder="Filter by Time Period"
            labeltext="Filter by: "
            value={selectedFrequency}
            handleChange={handleChangeFrequency}
            disabled={checkFrequencyDisabled() || Boolean(renderingChart)}
            minWidth="75px !important"
            hideSelected
            inlineLabel="Frequency:"
          />
        </ControlsSection>
      )}

      {manifestData && manifestData.ui.trend_chart.type && (
        <ControlsSection>
          <TrendsChartSelectField
            options={manifestData.ui.trend_chart.type.values.map((n) => n.display_name)}
            label=""
            placeholder="Filter by Type"
            labeltext="Filter by: "
            value={selectedType}
            handleChange={handleChangeType}
            minWidth="75px !important"
            hideSelected
            disabled={Boolean(renderingChart)}
            inlineLabel={`${manifestData.ui.trend_chart.type.display_name}:`}
          />
        </ControlsSection>
      )}

      <ControlsSection>
        <Box className="data-source-select-wrapper">
          <LibraryDataSourceSelect
            customClass="trends-source"
            label=""
            inlineLabel="Data Sources:"
            dataSourceOptions={
              isSnowflake
                ? [
                    allOption,
                    ...dataSourceOptions.map((c) => ({
                      ...c,
                      name: c.name || ''
                    }))
                  ]
                : [allOption, ...dataSourceOptions]
            }
            allOptionText={allOptionText}
            placeholder="Data Source"
            value={dataSourceValue}
            manifestData={manifestData}
            handleChange={handleChangeDataSource}
            disabled={chartData?.status !== DataStatus.Ready || Boolean(renderingChart)}
            formControlMinWidth={[allOption, ...dataSourceOptions].length > 4 ? '200px !important' : ''}
            disablePointerEvents={Boolean(renderingChart)}
          />
        </Box>
      </ControlsSection>

      {!predictionModalOpen && isDirty && (
        <ThemeButton
          themevariant="secondary"
          disableRipple
          onClick={handleResetAll}
          disabled={chartData?.status !== DataStatus.Ready || Boolean(renderingChart)}
          sx={{
            alignSelf: 'flex-end',
            color: `${theme.colors.textPrimary} !important`,
            '&.Mui-disabled': {
              background: 'transparent !important',
              opacity: '0.38 !important'
            }
          }}
        >
          Reset all
        </ThemeButton>
      )}
    </DataTrendsControlsContainer>
  )
}

export default DataTrendsControls
